// FRE-293: "Upload your first file" — section 1 of the Get started drawer,
// reused inline as the file-list empty-state card.
//
// Owns:
//  - Inline "Generate a token" form (org dropdown + scope checkboxes →
//    POST /api/v1/onboarding/mint-token via lib/mintToken.ts).
//  - API + CLI tab snippets that interpolate the minted token once available.
//  - Auto-collapse to a single ✓ row once the org has ≥1 file (re-expandable).
//
// The same component renders in two shells: a slim drawer section
// (`variant="drawer"`) and a full empty-state card (`variant="inline-card"`).
// Layout differs only in the outer frame; the inner form + tabs + token box
// are identical to keep the visual model coherent across both surfaces.

import { Check } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CopyButton } from "@/components/CopyButton.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getActiveOrgId } from "@/lib/activeOrg.ts";
import { ApiError, UnauthorizedError } from "@/lib/api.ts";
import { fetchHasFiles, getCachedHasFiles } from "@/lib/fileCount.ts";
import {
  INLINE_MINTABLE_SCOPES,
  type InlineMintableScope,
  type MintTokenResponse,
  mintToken,
  validateMintForm,
} from "@/lib/mintToken.ts";

type OrgOption = { id: string; slug: string; name: string };

export type UploadYourFirstFileSectionProps = {
  organizations: OrgOption[];
  // Worker's reported active org from /api/v1/me. Combined with the localStorage
  // hint to pick the dropdown's initial value.
  defaultOrganizationId: string | null;
  host: string;
  variant: "drawer" | "inline-card";
};

type ActiveTab = "api" | "cli";

export function UploadYourFirstFileSection({
  organizations,
  defaultOrganizationId,
  host,
  variant,
}: UploadYourFirstFileSectionProps) {
  const [tab, setTab] = useState<ActiveTab>("api");
  const [token, setToken] = useState<MintTokenResponse | null>(null);

  // File-count probe — used to auto-collapse the section once the user has
  // uploaded their first file. Treat "unknown" as "not yet" so we don't flash
  // a collapsed UI while the probe is in flight.
  const initialActiveOrgId = useMemo(() => getActiveOrgId(), []);
  const [hasFiles, setHasFiles] = useState<boolean>(() => {
    return getCachedHasFiles(initialActiveOrgId) === true;
  });
  // Manual override — clicking the ✓ collapsed row re-expands. Starts `null`
  // (follow the auto-collapse signal); flips to `true`/`false` once the user
  // expresses a preference.
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const activeOrgId = getActiveOrgId();
    const cached = getCachedHasFiles(activeOrgId);
    if (cached !== null) {
      setHasFiles(cached);
      return () => {
        cancelled = true;
      };
    }
    void fetchHasFiles(activeOrgId)
      .then((value) => {
        if (cancelled) return;
        setHasFiles(value);
      })
      .catch(() => {
        // Decorative: if the probe fails (offline, 401 during transition) we
        // just stay expanded. The full list on `/` will surface real errors.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve the collapsed/expanded view. The empty-state card variant should
  // never collapse — the whole reason it's on screen is that files=0. The
  // drawer variant honours the auto signal + manual override.
  const collapsed = useMemo(() => {
    if (variant === "inline-card") return false;
    if (expandedOverride === true) return false;
    if (expandedOverride === false) return true;
    return hasFiles;
  }, [variant, expandedOverride, hasFiles]);

  if (collapsed) {
    return <CollapsedRow onExpand={() => setExpandedOverride(true)} subtitle="1 file uploaded" />;
  }

  return (
    <SectionFrame variant={variant}>
      <SectionHeader variant={variant} />
      <McpHint />
      <GenerateTokenForm
        organizations={organizations}
        defaultOrganizationId={defaultOrganizationId}
        onToken={(next) => setToken(next)}
        token={token}
      />
      <Tabs value={tab} onValueChange={(next) => setTab(next as ActiveTab)} className="mt-4">
        <TabsList>
          <TabsTrigger value="api">API</TabsTrigger>
          <TabsTrigger value="cli">CLI</TabsTrigger>
        </TabsList>
        <TabsContent value="api" className="mt-3">
          <ApiTabSnippet host={host} token={token?.token ?? null} />
        </TabsContent>
        <TabsContent value="cli" className="mt-3">
          <CliTabSnippet host={host} token={token?.token ?? null} />
        </TabsContent>
      </Tabs>
    </SectionFrame>
  );
}

function SectionFrame({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: "drawer" | "inline-card";
}) {
  if (variant === "drawer") {
    return <section className="mb-6">{children}</section>;
  }
  return (
    <section className="rounded-md border border-border bg-card px-5 py-5">{children}</section>
  );
}

function SectionHeader({ variant }: { variant: "drawer" | "inline-card" }) {
  return (
    <>
      <h2
        className={
          variant === "inline-card"
            ? "text-lg font-semibold text-foreground"
            : "text-sm font-semibold text-foreground"
        }
      >
        Upload your first file
      </h2>
      {variant === "inline-card" ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Mint a token, then push a file via the API or the CLI.
        </p>
      ) : null}
    </>
  );
}

function McpHint() {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Prefer MCP? Set up your assistant in <em>Connect a client</em> below — then ask it to upload.
    </p>
  );
}

// "Success" pill colors aren't in the shadcn token palette (no semantic
// success/warning). Keep emerald + amber as Tailwind literals with explicit
// dark-mode counterparts so they remain legible across themes.
function CollapsedRow({ onExpand, subtitle }: { onExpand: () => void; subtitle: string }) {
  return (
    <section className="mb-6 flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900 dark:bg-emerald-950/30">
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-100"
        >
          <Check className="size-3" strokeWidth={3} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            Upload your first file
          </p>
          <p className="truncate text-xs text-emerald-800 dark:text-emerald-200">{subtitle}</p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={onExpand}
        className="border-emerald-300 bg-card text-emerald-900 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-100 dark:hover:bg-emerald-900"
      >
        Show again
      </Button>
    </section>
  );
}

function GenerateTokenForm({
  organizations,
  defaultOrganizationId,
  onToken,
  token,
}: {
  organizations: OrgOption[];
  defaultOrganizationId: string | null;
  onToken: (next: MintTokenResponse | null) => void;
  token: MintTokenResponse | null;
}) {
  // Pick the dropdown's initial value: localStorage hint (what the worker
  // actually sees on every request) → /me's reported active org → first org.
  // Same precedence as the header OrgSwitcher.
  const initial = useMemo(() => {
    const hint = getActiveOrgId();
    if (hint && organizations.some((o) => o.id === hint)) return hint;
    if (defaultOrganizationId && organizations.some((o) => o.id === defaultOrganizationId)) {
      return defaultOrganizationId;
    }
    return organizations[0]?.id ?? null;
  }, [organizations, defaultOrganizationId]);

  const [organizationId, setOrganizationId] = useState<string | null>(initial);
  const [scopes, setScopes] = useState<Set<InlineMintableScope>>(
    () => new Set<InlineMintableScope>(INLINE_MINTABLE_SCOPES),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = useCallback((scope: InlineMintableScope) => {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (busy) return;
      const validation = validateMintForm({ organizationId, scopes });
      if (!validation.ok) {
        setError(
          validation.error === "organization_required"
            ? "Pick an organization."
            : "Select at least one scope.",
        );
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const response = await mintToken(validation.request);
        onToken(response);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          setError("You're signed out — refresh and sign in again.");
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Failed to generate token");
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, organizationId, scopes, onToken],
  );

  // If a token's already been minted, hide the form and show the token box
  // with a "Generate another" reset.
  if (token) {
    return <MintedTokenBox token={token} onReset={() => onToken(null)} />;
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="mt-3 space-y-3">
      {error ? (
        <Alert variant="destructive" className="text-xs">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div>
        <label htmlFor="upload-mint-org" className="block text-xs font-medium text-foreground">
          Organization
        </label>
        <select
          id="upload-mint-org"
          value={organizationId ?? ""}
          onChange={(e) => setOrganizationId(e.target.value || null)}
          disabled={busy || organizations.length === 0}
          className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
        >
          {organizations.length === 0 ? (
            <option value="">No organizations</option>
          ) : (
            organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.slug})
              </option>
            ))
          )}
        </select>
      </div>
      <fieldset>
        <legend className="text-xs font-medium text-foreground">Scopes</legend>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {INLINE_MINTABLE_SCOPES.map((scope) => (
            <label key={scope} className="inline-flex items-center gap-1.5 text-sm text-foreground">
              <input
                type="checkbox"
                checked={scopes.has(scope)}
                onChange={() => toggleScope(scope)}
                disabled={busy}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <code className="text-xs">{scope}</code>
            </label>
          ))}
        </div>
      </fieldset>
      <Button type="submit" size="sm" disabled={busy || !organizationId || scopes.size === 0}>
        {busy ? "Generating…" : "Generate token"}
      </Button>
    </form>
  );
}

function MintedTokenBox({ token, onReset }: { token: MintTokenResponse; onReset: () => void }) {
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 dark:border-amber-900 dark:bg-amber-950/30">
      <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
        Copy this token — you won't see it again.
      </p>
      <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">
        Save it somewhere safe (a password manager, a `.env`, your shell config). We hash it on the
        server and can't show it again.
      </p>
      <div className="mt-2 flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md border border-amber-200 bg-card px-2.5 py-2 font-mono text-[11px] leading-snug text-foreground dark:border-amber-900">
          <code>{token.token}</code>
        </pre>
        <CopyButton value={token.token} label="Copy token" size="xs" />
      </div>
      <p className="mt-2 text-[11px] text-amber-800 dark:text-amber-200">
        Scopes: {token.scopes.map((s) => `\`${s}\``).join(", ")}.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-2 text-[11px] font-medium text-amber-900 underline hover:no-underline dark:text-amber-100"
      >
        Generate another token
      </button>
    </div>
  );
}

// Placeholder rendered in the snippet box before a token is minted. We still
// show the curl/CLI structure so users know what they're working towards.
const TOKEN_PLACEHOLDER = "drop_…";

function ApiTabSnippet({ host, token }: { host: string; token: string | null }) {
  const tokenValue = token ?? TOKEN_PLACEHOLDER;
  const snippet = `curl -X POST ${host}/api/v1/files \\\n  -H 'authorization: Bearer ${tokenValue}' \\\n  -F file=@./your-file.html`;
  return (
    <SnippetBlock
      headline={token ? "Run this in your terminal" : "Generate a token above, then run this."}
      snippet={snippet}
    />
  );
}

function CliTabSnippet({ host, token }: { host: string; token: string | null }) {
  // No GH release yet — match README's "build from source" path. Inline the
  // API URL + token via `DROP_API_URL` / `DROP_TOKEN` env hints so users don't
  // have to walk through the interactive wizard.
  const tokenValue = token ?? TOKEN_PLACEHOLDER;
  const snippet =
    `# Build the binary (one-time)\n` +
    `pnpm cli:build\n` +
    `\n` +
    `# Configure (first run prompts for API URL + token, or set them inline)\n` +
    `DROP_API_URL=${host} DROP_TOKEN=${tokenValue} ./apps/cli/dist/fileservice push ./your-file.html`;
  return (
    <SnippetBlock
      headline={token ? "Run this from the repo root" : "Generate a token above, then run this."}
      snippet={snippet}
    />
  );
}

function SnippetBlock({ headline, snippet }: { headline: string; snippet: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{headline}</p>
      <div className="mt-1.5 flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md border border-border bg-muted px-2.5 py-2 text-[11px] leading-snug text-foreground">
          <code>{snippet}</code>
        </pre>
        <CopyButton value={snippet} label="Copy" size="xs" />
      </div>
    </div>
  );
}
